import React from 'react';
import { 
  Typography, 
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled
} from '@mui/material';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: '#fff',
  fontSize: '0.8rem',
  padding: theme.spacing(1),
  borderBottom: '1px solid #333',
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: '#333',
  fontWeight: 'bold',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
}));

interface StandardPricingTableProps {
  service: any;
}

const StandardPricingTable: React.FC<StandardPricingTableProps> = ({ service }) => {
  
  
  
  // Define which columns to show based on service type
  const getColumnVisibility = (serviceCode: string) => {
    // Handle Pune Local variations (PUNE_LOCAL, PUNE_LOCAL_6H, PUNE_LOCAL_9H, PUNE_LOCAL_12H)
    // if (serviceCode.startsWith('PUNE_LOCAL')) {
    //   return {
    //     showRatePerKm: false,
    //     showDriverAllowance: false,
    //     showExtraKmRate: true,
    //     showOfferPrice: false
    //   };
    // }
    
    switch (serviceCode) {
      case 'ONE_WAY':        // One Way
        return {
          showRatePerKm: false,
          showDriverAllowance: false,  // Remove driver allowance from One Way
          showExtraKmRate: false,
          showOfferPrice: true
        };
      case 'OUTSTATION_ROUND':  // Outstation Round Trip
        return {
          showRatePerKm: true,
          showDriverAllowance: true,   // Keep driver allowance only for Round Trip
          showExtraKmRate: false,
          showOfferPrice: false        // Remove offer price from Outstation Round Trip
        };
      case 'MUMBAI_AIRPORT':           // Mumbai Airport
        return {
          showRatePerKm: false,
          showDriverAllowance: false,
          showExtraKmRate: false,    // Remove Rate/Extra KM from Mumbai Airport
          showOfferPrice: true
        };
      default:
        return {
          showRatePerKm: false,
          showDriverAllowance: false,
          showExtraKmRate: false,
          showOfferPrice: true
        };
    }
  };

  const { showRatePerKm, showDriverAllowance, showExtraKmRate, showOfferPrice } = getColumnVisibility(service.code);
  
  return (
    <TableContainer component={Paper} sx={{ backgroundColor: '#2a2a2a' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <StyledTableHeadCell>Car Type</StyledTableHeadCell>
            <StyledTableHeadCell>Make & Model</StyledTableHeadCell>
            <StyledTableHeadCell>Capacity</StyledTableHeadCell>
            {/* <StyledTableHeadCell>Fuel Type</StyledTableHeadCell> */}
            <StyledTableHeadCell align="right">Price (₹)</StyledTableHeadCell>
            {showOfferPrice && <StyledTableHeadCell align="right" sx={{ color: '#4caf50' }}>Offer Price (₹)</StyledTableHeadCell>}
            <StyledTableHeadCell align="right">Minimum Price (₹)</StyledTableHeadCell>
            {showRatePerKm && <StyledTableHeadCell align="right">Rate/KM (₹)</StyledTableHeadCell>}
            {showDriverAllowance && <StyledTableHeadCell align="right">Driver Allowance (₹)</StyledTableHeadCell>}
            {showExtraKmRate && <StyledTableHeadCell align="right">Rate/Extra KM (₹)</StyledTableHeadCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {service.carTypePricing.map((pricing: any) => (
            <StyledTableRow key={pricing.carTypeId}>
              <StyledTableCell>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'bold',
                    color: (() => {
                      // Assign different colors based on car type
                      const carType = pricing.carType.toLowerCase();
                      if (carType.includes('sedan')) return '#42a5f5'; // Blue for Sedan
                      if (carType.includes('suv')) return '#ffb74d'; // Orange for SUV
                      if (carType.includes('premium')) return '#ba68c8'; // Purple for Premium
                      if (carType.includes('luxury')) return '#ffd54f'; // Yellow for Luxury
                      if (carType.includes('hatchback')) return '#4db6ac'; // Teal for Hatchback
                      return '#e0e0e0'; // Default light gray
                    })(),
                    backgroundColor: (() => {
                      // Assign different background colors based on car type
                      const carType = pricing.carType.toLowerCase();
                      if (carType.includes('sedan')) return 'rgba(66, 165, 245, 0.15)'; // Light blue bg
                      if (carType.includes('suv')) return 'rgba(255, 183, 77, 0.15)'; // Light orange bg
                      if (carType.includes('premium')) return 'rgba(186, 104, 200, 0.15)'; // Light purple bg
                      if (carType.includes('luxury')) return 'rgba(255, 213, 79, 0.15)'; // Light yellow bg
                      if (carType.includes('hatchback')) return 'rgba(77, 182, 172, 0.15)'; // Light teal bg
                      return 'transparent'; // Default transparent
                    })(),
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    display: 'inline-block'
                  }}
                >
                  {pricing.carType}
                </Typography>
              </StyledTableCell>
              <StyledTableCell>{pricing.makeCompany} {pricing.model}</StyledTableCell>
              <StyledTableCell>{pricing.capacity - 1}</StyledTableCell>
              {/* <StyledTableCell>{pricing.fuelType}</StyledTableCell> */}
              <StyledTableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {pricing.basePrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </Typography>
              </StyledTableCell>
              {showOfferPrice && (
  <StyledTableCell align="right">
    {pricing.offerInfo ? (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row',
        alignItems: 'center',
        gap: 0.5, // Reduced gap
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        borderRadius: 1,
        p: 0.3, // Reduced padding
        border: '1px dashed rgba(76, 175, 80, 0.3)',
        maxWidth: 'fit-content', // Limit width to content
        ml: 'auto' // Align to right
      }}>
        <Typography variant="body2" sx={{ 
          color: '#4caf50', 
          fontWeight: 'bold',
          fontSize: '0.85rem' // Slightly smaller font
        }}>
          {pricing.offerInfo.discountedPrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
        </Typography>
        <Typography variant="caption" sx={{ 
          color: '#4caf50', 
          fontWeight: 'bold',
          backgroundColor: 'rgba(124, 207, 126, 0.2)',
          px: 0.3, // Reduced horizontal padding
          py: 0.15, // Reduced vertical padding
          borderRadius: 0.5,
          fontSize: '0.65rem', // Smaller font size
          whiteSpace: 'nowrap' // Prevent text wrapping
        }}>
           Discount ₹{(pricing.basePrice - pricing.offerInfo.discountedPrice).toLocaleString('en-IN')}
        </Typography>
      </Box>
    ) : (
      '-'
    )}
  </StyledTableCell>
)}
              <StyledTableCell align="right">
                {pricing.negotiationPrice ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'flex-end'
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: '#ff9800', 
                      fontWeight: 'bold',
                      fontSize: '0.85rem'
                    }}>
                      {pricing.negotiationPrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </Typography>
                    {pricing.basePrice > pricing.negotiationPrice && (
                      <Typography variant="caption" sx={{ 
                        color: '#4caf50', 
                        fontWeight: 'bold',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        // px: 0.75,
                        // py: 0.25,
                        // borderRadius: 0.5,
                        mt: 0.5
                      }}>
                        {/* SAVE {((pricing.basePrice - pricing.negotiationPrice) / pricing.basePrice * 100).toFixed(0)}% */}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  '-'
                )}
              </StyledTableCell>
              {showRatePerKm && <StyledTableCell align="right">{pricing.ratePerKm || '-'}</StyledTableCell>}
              {showDriverAllowance && <StyledTableCell align="right">{pricing.driverAllowance || '-'}</StyledTableCell>}
              {showExtraKmRate && <StyledTableCell align="right">{pricing.ratePerExtraKm || '-'}</StyledTableCell>}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default StandardPricingTable;
